import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AccountManagement = () => {
  const [profileData, setProfileData] = useState({
    firstName: 'John',
    lastName: 'Trader',
    email: 'john@cryptowizard.com',
    phone: '+1 (555) 123-4567',
    country: 'United States',
    timezone: 'America/New_York',
    language: 'English',
    tradingExperience: 'intermediate',
    riskTolerance: 'medium',
    investmentGoals: 'growth'
  });

  const [subscriptionData, setSubscriptionData] = useState({
    plan: 'Professional',
    status: 'Active',
    billingCycle: 'Monthly',
    nextBilling: '2024-02-20',
    amount: '$49.99',
    features: [
      'Advanced PTS-WIZARD-V3 Algorithm',
      'Real-time Market Data',
      'Unlimited Watchlists',
      'Portfolio Analytics',
      'API Access',
      'Priority Support'
    ]
  });

  const [dataExport, setDataExport] = useState({
    includePortfolio: true,
    includeTrades: true,
    includeAlerts: true,
    includeSettings: true,
    dateRange: '1year',
    format: 'json'
  });

  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const countryOptions = [
    { value: 'United States', label: 'United States' },
    { value: 'Canada', label: 'Canada' },
    { value: 'United Kingdom', label: 'United Kingdom' },
    { value: 'Germany', label: 'Germany' },
    { value: 'France', label: 'France' },
    { value: 'Japan', label: 'Japan' },
    { value: 'Australia', label: 'Australia' }
  ];

  const languageOptions = [
    { value: 'English', label: 'English' },
    { value: 'Spanish', label: 'Español' },
    { value: 'French', label: 'Français' },
    { value: 'German', label: 'Deutsch' },
    { value: 'Japanese', label: '日本語' },
    { value: 'Chinese', label: '中文' }
  ];

  const experienceOptions = [
    { value: 'beginner', label: 'Beginner (< 1 year)' },
    { value: 'intermediate', label: 'Intermediate (1-3 years)' },
    { value: 'advanced', label: 'Advanced (3-5 years)' },
    { value: 'expert', label: 'Expert (5+ years)' }
  ];

  const riskToleranceOptions = [
    { value: 'low', label: 'Conservative (Low Risk)' },
    { value: 'medium', label: 'Moderate (Medium Risk)' },
    { value: 'high', label: 'Aggressive (High Risk)' }
  ];

  const investmentGoalOptions = [
    { value: 'preservation', label: 'Capital Preservation' },
    { value: 'income', label: 'Income Generation' },
    { value: 'growth', label: 'Capital Growth' },
    { value: 'speculation', label: 'Speculation' }
  ];

  const dateRangeOptions = [
    { value: '1month', label: 'Last Month' },
    { value: '3months', label: 'Last 3 Months' },
    { value: '6months', label: 'Last 6 Months' },
    { value: '1year', label: 'Last Year' },
    { value: 'all', label: 'All Time' }
  ];

  const formatOptions = [
    { value: 'json', label: 'JSON Format' },
    { value: 'csv', label: 'CSV Format' },
    { value: 'xlsx', label: 'Excel Format' },
    { value: 'pdf', label: 'PDF Report' }
  ];

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = () => {
    setIsEditingProfile(false);
    // Save profile logic here
  };

  const handleCancelEdit = () => {
    setIsEditingProfile(false);
    // Reset to original data
  };

  const handleExportData = () => {
    const exportData = {
      profile: profileData,
      subscription: subscriptionData,
      exportSettings: dataExport,
      exportDate: new Date()?.toISOString()
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cryptowizard-data-export-${new Date()?.toISOString()?.split('T')?.[0]}.${dataExport?.format}`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDeleteAccount = () => {
    // Account deletion logic here
    console.log('Account deletion requested');
    setShowDeleteConfirm(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Account Management</h3>
          <p className="text-sm text-muted-foreground">Manage your profile, subscription, and account data</p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-2 px-3 py-1 bg-success/10 text-success rounded-md">
            <Icon name="CheckCircle" size={16} />
            <span className="text-sm font-medium">Verified</span>
          </div>
        </div>
      </div>
      {/* Profile Information */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-md font-medium text-foreground">Profile Information</h4>
            <p className="text-sm text-muted-foreground">Update your personal information and preferences</p>
          </div>
          <Button
            variant={isEditingProfile ? "outline" : "default"}
            size="sm"
            onClick={() => setIsEditingProfile(!isEditingProfile)}
            iconName={isEditingProfile ? "X" : "Edit"}
            iconPosition="left"
          >
            {isEditingProfile ? 'Cancel' : 'Edit Profile'}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="First Name"
            value={profileData?.firstName}
            onChange={(e) => handleProfileChange('firstName', e?.target?.value)}
            disabled={!isEditingProfile}
          />
          <Input
            label="Last Name"
            value={profileData?.lastName}
            onChange={(e) => handleProfileChange('lastName', e?.target?.value)}
            disabled={!isEditingProfile}
          />
          <Input
            label="Email Address"
            type="email"
            value={profileData?.email}
            onChange={(e) => handleProfileChange('email', e?.target?.value)}
            disabled={!isEditingProfile}
            description="Used for login and notifications"
          />
          <Input
            label="Phone Number"
            type="tel"
            value={profileData?.phone}
            onChange={(e) => handleProfileChange('phone', e?.target?.value)}
            disabled={!isEditingProfile}
            description="For SMS alerts and support"
          />
          <Select
            label="Country"
            options={countryOptions}
            value={profileData?.country}
            onChange={(value) => handleProfileChange('country', value)}
            disabled={!isEditingProfile}
          />
          <Select
            label="Language"
            options={languageOptions}
            value={profileData?.language}
            onChange={(value) => handleProfileChange('language', value)}
            disabled={!isEditingProfile}
          />
        </div>

        {isEditingProfile && (
          <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-border">
            <Button variant="default" onClick={handleSaveProfile} iconName="Save" iconPosition="left">
              Save Changes
            </Button>
            <Button variant="outline" onClick={handleCancelEdit}>
              Cancel
            </Button>
          </div>
        )}
      </div>
      {/* Trading Profile */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Trading Profile</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            label="Trading Experience"
            description="Your experience level with cryptocurrency trading"
            options={experienceOptions}
            value={profileData?.tradingExperience}
            onChange={(value) => handleProfileChange('tradingExperience', value)}
          />
          <Select
            label="Risk Tolerance"
            description="Your comfort level with investment risk"
            options={riskToleranceOptions}
            value={profileData?.riskTolerance}
            onChange={(value) => handleProfileChange('riskTolerance', value)}
          />
          <Select
            label="Investment Goals"
            description="Your primary investment objective"
            options={investmentGoalOptions}
            value={profileData?.investmentGoals}
            onChange={(value) => handleProfileChange('investmentGoals', value)}
          />
        </div>
      </div>
      {/* Subscription Details */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-md font-medium text-foreground">Subscription Details</h4>
            <p className="text-sm text-muted-foreground">Manage your CryptoWizard Pro subscription</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-2 px-3 py-1 bg-success/10 text-success rounded-md">
              <Icon name="Crown" size={16} />
              <span className="text-sm font-medium">{subscriptionData?.plan}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted rounded-md">
              <span className="text-sm text-muted-foreground">Plan Status</span>
              <span className="text-sm font-medium text-success">{subscriptionData?.status}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted rounded-md">
              <span className="text-sm text-muted-foreground">Billing Cycle</span>
              <span className="text-sm font-medium text-foreground">{subscriptionData?.billingCycle}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted rounded-md">
              <span className="text-sm text-muted-foreground">Next Billing</span>
              <span className="text-sm font-medium text-foreground">{subscriptionData?.nextBilling}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted rounded-md">
              <span className="text-sm text-muted-foreground">Amount</span>
              <span className="text-sm font-medium text-foreground">{subscriptionData?.amount}</span>
            </div>
          </div>
          
          <div className="space-y-3">
            <h5 className="text-sm font-medium text-foreground">Included Features</h5>
            <div className="space-y-2">
              {subscriptionData?.features?.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Icon name="Check" size={14} className="text-success" />
                  <span className="text-sm text-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2 mt-6 pt-4 border-t border-border">
          <Button variant="default" iconName="CreditCard" iconPosition="left">
            Update Payment Method
          </Button>
          <Button variant="outline" iconName="RefreshCw" iconPosition="left">
            Change Plan
          </Button>
          <Button variant="outline" iconName="Download" iconPosition="left">
            Download Invoice
          </Button>
        </div>
      </div>
      {/* Data Export */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-md font-medium text-foreground">Data Export</h4>
            <p className="text-sm text-muted-foreground">Export your account data and trading history</p>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={handleExportData}
            iconName="Download"
            iconPosition="left"
          >
            Export Data
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">Data to Include</label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={dataExport?.includePortfolio}
                    onChange={(e) => setDataExport(prev => ({ ...prev, includePortfolio: e?.target?.checked }))}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-foreground">Portfolio Data</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={dataExport?.includeTrades}
                    onChange={(e) => setDataExport(prev => ({ ...prev, includeTrades: e?.target?.checked }))}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-foreground">Trading History</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={dataExport?.includeAlerts}
                    onChange={(e) => setDataExport(prev => ({ ...prev, includeAlerts: e?.target?.checked }))}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-foreground">Alert History</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={dataExport?.includeSettings}
                    onChange={(e) => setDataExport(prev => ({ ...prev, includeSettings: e?.target?.checked }))}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-foreground">Settings & Preferences</span>
                </label>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <Select
              label="Date Range"
              description="Select the time period for data export"
              options={dateRangeOptions}
              value={dataExport?.dateRange}
              onChange={(value) => setDataExport(prev => ({ ...prev, dateRange: value }))}
            />
            <Select
              label="Export Format"
              description="Choose the file format for export"
              options={formatOptions}
              value={dataExport?.format}
              onChange={(value) => setDataExport(prev => ({ ...prev, format: value }))}
            />
          </div>
        </div>
      </div>
      {/* Account Actions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Account Actions</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <h5 className="text-sm font-medium text-foreground">Account Security</h5>
              <div className="space-y-2">
                <Button variant="outline" fullWidth iconName="Key" iconPosition="left">
                  Change Password
                </Button>
                <Button variant="outline" fullWidth iconName="Mail" iconPosition="left">
                  Change Email Address
                </Button>
                <Button variant="outline" fullWidth iconName="Smartphone" iconPosition="left">
                  Update Phone Number
                </Button>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <h5 className="text-sm font-medium text-foreground">Account Management</h5>
              <div className="space-y-2">
                <Button variant="outline" fullWidth iconName="Pause" iconPosition="left">
                  Suspend Account
                </Button>
                <Button variant="outline" fullWidth iconName="Download" iconPosition="left">
                  Request Data Copy
                </Button>
                <Button
                  variant="destructive"
                  fullWidth
                  onClick={() => setShowDeleteConfirm(true)}
                  iconName="Trash2"
                  iconPosition="left"
                >
                  Delete Account
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-destructive/10 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-destructive" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-foreground">Delete Account</h4>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-destructive/5 border border-destructive/20 rounded-md p-4">
                <p className="text-sm text-foreground mb-2">
                  Deleting your account will permanently remove:
                </p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• All portfolio data and trading history</li>
                  <li>• Custom alerts and watchlists</li>
                  <li>• Algorithm configurations and settings</li>
                  <li>• Account preferences and data</li>
                </ul>
              </div>
              
              <Input
                label="Type 'DELETE' to confirm"
                placeholder="DELETE"
                description="This confirmation is required to proceed"
              />
              
              <div className="flex items-center space-x-2">
                <Button variant="destructive" onClick={handleDeleteAccount} className="flex-1">
                  Delete Account
                </Button>
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Save Actions */}
      <div className="flex items-center justify-end space-x-4 pt-4 border-t border-border">
        <Button variant="outline">
          Reset All Settings
        </Button>
        <Button variant="default" iconName="Save" iconPosition="left">
          Save All Changes
        </Button>
      </div>
    </div>
  );
};

export default AccountManagement;